// エラーハンドリングユーティリティ
import type { AppError } from '../types/app.types.js';
import { AppConfig } from '../config.js';

export class ErrorHandler {
  private static instance: ErrorHandler;
  private errorHistory: AppError[] = [];
  private readonly maxHistorySize = 50;

  private constructor() {}

  static getInstance(): ErrorHandler {
    if (!ErrorHandler.instance) {
      ErrorHandler.instance = new ErrorHandler();
    }
    return ErrorHandler.instance;
  }

  // エラー作成メソッド
  createError(
    type: AppError['type'],
    code: string,
    message: string,
    options: {
      suggestion?: string;
      technical?: string;
      retryable?: boolean;
      severity?: AppError['severity'];
      context?: Record<string, any>;
    } = {}
  ): AppError {
    const error: AppError = {
      type,
      code,
      message,
      suggestion: options.suggestion,
      technical: options.technical,
      timestamp: new Date(),
      retryable: options.retryable ?? this.isRetryableByType(type),
      severity: options.severity ?? this.getSeverityByType(type),
      context: options.context
    };

    this.logError(error);
    this.addToHistory(error);
    
    return error;
  }

  // 入力値検証エラー
  createValidationError(field: string, value: any, constraint: string): AppError {
    return this.createError(
      'validation',
      `VALIDATION_${field.toUpperCase()}_${constraint.toUpperCase()}`,
      this.getValidationMessage(field, constraint),
      {
        suggestion: this.getValidationSuggestion(field, constraint),
        context: { field, value, constraint },
        severity: 'medium'
      }
    );
  }

  // アルゴリズムエラー
  createAlgorithmError(phase: string, details: string): AppError {
    return this.createError(
      'algorithm',
      `ALGORITHM_${phase.toUpperCase()}_FAILED`,
      `画像生成の${phase}フェーズでエラーが発生しました`,
      {
        suggestion: 'パラメータを調整して再試行してください',
        technical: details,
        context: { phase },
        severity: 'high'
      }
    );
  }

  // メモリエラー
  createMemoryError(requiredMemory: number, availableMemory?: number): AppError {
    return this.createError(
      'memory',
      'MEMORY_INSUFFICIENT',
      'メモリが不足しています',
      {
        suggestion: 'より小さなサイズの画像を生成してください',
        technical: `必要メモリ: ${this.formatBytes(requiredMemory)}${availableMemory ? `, 利用可能: ${this.formatBytes(availableMemory)}` : ''}`,
        context: { requiredMemory, availableMemory },
        severity: 'high'
      }
    );
  }

  // ブラウザ互換性エラー
  createBrowserError(feature: string): AppError {
    return this.createError(
      'browser',
      `BROWSER_${feature.toUpperCase()}_UNSUPPORTED`,
      `お使いのブラウザは${feature}をサポートしていません`,
      {
        suggestion: 'モダンなブラウザをご利用ください（Chrome、Firefox、Safari、Edge）',
        context: { feature, userAgent: navigator.userAgent },
        severity: 'critical'
      }
    );
  }

  // タイムアウトエラー
  createTimeoutError(operation: string, timeout: number): AppError {
    return this.createError(
      'timeout',
      `TIMEOUT_${operation.toUpperCase()}`,
      `${operation}がタイムアウトしました`,
      {
        suggestion: 'より小さなサイズで試すか、しばらく待ってから再試行してください',
        technical: `タイムアウト時間: ${timeout}ms`,
        context: { operation, timeout },
        severity: 'medium'
      }
    );
  }

  // 未知のエラー
  createUnknownError(originalError: Error | unknown): AppError {
    const errorMessage = originalError instanceof Error ? originalError.message : String(originalError);
    const errorStack = originalError instanceof Error ? originalError.stack : undefined;

    return this.createError(
      'unknown',
      'UNKNOWN_ERROR',
      '予期しないエラーが発生しました',
      {
        suggestion: 'ページを再読み込みして再試行してください',
        technical: errorMessage,
        context: { 
          originalError: errorMessage,
          stack: errorStack,
          timestamp: Date.now()
        },
        severity: 'high'
      }
    );
  }

  // エラーの重複チェック
  isDuplicateError(error: AppError, withinMinutes: number = 5): boolean {
    const cutoffTime = new Date(Date.now() - withinMinutes * 60 * 1000);
    return this.errorHistory.some(historyError => 
      historyError.code === error.code &&
      historyError.timestamp > cutoffTime
    );
  }

  // エラー履歴の取得
  getErrorHistory(limit?: number): AppError[] {
    return limit ? this.errorHistory.slice(-limit) : [...this.errorHistory];
  }

  // エラー統計の取得
  getErrorStats(): {
    total: number;
    byType: Record<string, number>;
    bySeverity: Record<string, number>;
    recentCount: number;
  } {
    const recentCutoff = new Date(Date.now() - 24 * 60 * 60 * 1000); // 24時間前
    
    return {
      total: this.errorHistory.length,
      byType: this.countBy(this.errorHistory, 'type'),
      bySeverity: this.countBy(this.errorHistory, 'severity'),
      recentCount: this.errorHistory.filter(e => e.timestamp > recentCutoff).length
    };
  }

  // エラー履歴のクリア
  clearHistory(): void {
    this.errorHistory = [];
  }

  // プライベートメソッド
  private isRetryableByType(type: AppError['type']): boolean {
    switch (type) {
      case 'network':
      case 'timeout':
      case 'memory':
        return true;
      case 'browser':
      case 'validation':
        return false;
      case 'algorithm':
      case 'unknown':
        return true;
      default:
        return false;
    }
  }

  private getSeverityByType(type: AppError['type']): AppError['severity'] {
    switch (type) {
      case 'validation':
        return 'medium';
      case 'algorithm':
        return 'high';
      case 'browser':
        return 'critical';
      case 'memory':
        return 'high';
      case 'network':
        return 'medium';
      case 'timeout':
        return 'medium';
      case 'unknown':
        return 'high';
      default:
        return 'medium';
    }
  }

  private getValidationMessage(field: string, constraint: string): string {
    const messages: Record<string, Record<string, string>> = {
      targetSize: {
        required: 'ファイルサイズを入力してください',
        min: `ファイルサイズは${AppConfig.generation.minFileSize}バイト以上で入力してください`,
        max: `ファイルサイズは${AppConfig.generation.maxFileSize}バイト以下で入力してください`,
        invalid: 'ファイルサイズは正の整数で入力してください'
      }
    };

    return messages[field]?.[constraint] || `${field}の値が無効です`;
  }

  private getValidationSuggestion(field: string, constraint: string): string {
    const suggestions: Record<string, Record<string, string>> = {
      targetSize: {
        min: '最小100バイトから指定できます',
        max: '最大100MBまで指定できます',
        invalid: '数値のみを入力してください'
      }
    };

    return suggestions[field]?.[constraint] || '正しい値を入力してください';
  }

  private logError(error: AppError): void {
    const logLevel = this.getLogLevel(error.severity);
    const logMessage = `[${error.type.toUpperCase()}] ${error.code}: ${error.message}`;
    
    switch (logLevel) {
      case 'error':
        console.error(logMessage, error);
        break;
      case 'warn':
        console.warn(logMessage, error);
        break;
      case 'info':
        console.info(logMessage, error);
        break;
      default:
        console.log(logMessage, error);
    }
  }

  private getLogLevel(severity: AppError['severity']): 'error' | 'warn' | 'info' | 'log' {
    switch (severity) {
      case 'critical':
      case 'high':
        return 'error';
      case 'medium':
        return 'warn';
      case 'low':
        return 'info';
      default:
        return 'log';
    }
  }

  private addToHistory(error: AppError): void {
    this.errorHistory.push(error);
    
    // 履歴サイズの制限
    if (this.errorHistory.length > this.maxHistorySize) {
      this.errorHistory = this.errorHistory.slice(-this.maxHistorySize);
    }
  }

  private formatBytes(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  private countBy<T>(array: T[], key: keyof T): Record<string, number> {
    return array.reduce((acc, item) => {
      const value = String(item[key]);
      acc[value] = (acc[value] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);
  }
}

// シングルトンインスタンスのエクスポート
export const errorHandler = ErrorHandler.getInstance(); 